import { createSlice } from "@reduxjs/toolkit";
import { adminApi } from "Services/AdminService";
import { subscriptionPlanApi } from 'Services/SubscriptionPlanService'

export const initialState = {
  loadingList: false,
  openEnableDisableDialog: false,
  openExpirationDateDialog: false,
  openPipedriveImportDialog: false,
  userId: "",
  pipedriveOrganizationCode: {
    value: "",
    error: "",
  },
  isMasterUser: false,
  expirationDateDialog: {
    licenseExpirationDate: "",
    licenseDescription: "",
    expired: false,
    subscriptionPlanName: {
      value: "",
      error: "",
    },
    subscriptionPlanList: [],
  },
  search: {
    searchTerm: "",
    status: "",
  },
  pagedList: {
    pageNumber: 0,
    pageSize: 10,
    pageCount: 0,
    totalCount: 0,
    orderField: "createdAt",
    orderDirection: "desc",
    items: [],
  },
  additionalUsersList: [0, 1, 2, 3, 4, 5, 10, 20],
  bestowAccount: {
    openPopup: false,
    userId: "",
    userName: "",
    organizationName: "",
    additionalUsersAmount: 0,
    licenseAmount: 0,
  },
  restoreAccount: {
    openPopup: false,
    userId: "",
    userName: "",
  },
  reportUsage: {
    openPopup: false,
    userId: "",
    userName: "",
  },
  licenses: {
    openPopup: false,
    licensesList: [],
    accountInfo: {},
    usedLicenses: 0,
    totalLicenses: 0,
    availableLicenses: 0,
    licenseStatusMessage: "-"
  },
  salesDemoTemplate: {
    openPopup: false,
    userId: "",
    userName: "",
  },
  oneClickLogin: {
    openPopup: false,
    oneClickLoginUrl: "",
    userName: "",
    duration: ""
  },
  resendNotifications: {
    openPopup: false,
    userName: "",
    userId: "",
    selectedDate: null,
    notificationsCount: "-"
  },
  upgradeDialog: {
    openPopup: false,
    organizationName: "",
    email: "",
    contactName: "",
    pipedriveOrganizationCode: "",
    subscriptionPlanName: {
      value: "",
      error: "",
    },
    subscriptionPlanList: [],
    originalSubscriptionPlanList: [],
    additionalUsers: {
        value: '',
        error: ''
    },
    couponCode: {
        value: '',
        error: ''
    },
    paymentType: "Paid", //Payd, SendPaymentLink, SkipPayment, PayNow
    userId: "",
    organizationId: "",
    paymentCountryCode: "",
    skipPaymentReason: {
        value: "",
        error: ""
    },
    applyCoupon: null,
    coupon: {
      id: null,
      name: null,
      code: null,
      expirationDate: null,
      expirationDateFmt: null,
      isExtraTime: false,
      isDiscount: false,
      discountPercentual: null,
      extraMonths: null,
      discountType: null,
      unlockPlans: false,
      subscriptionPlanCouponList: null
    }
  }
};

const manageAccountSlice = createSlice({
  name: "manage-account",
  initialState,
  reducers: {
    handleChange: (state, action) => {
      state.search[action.payload.field] = action.payload.value;
    },
    handleChangeField: (state, action) => {
      state[action.payload.field].error = "";
      state[action.payload.field].value = action.payload.value;
    },
    handleOpenExpirationDateDialog: (state, action) => {
      state.userId = action.payload;
      state.openExpirationDateDialog = true;
    },
    handleCloseExpirationDateDialog: (state, action) => {
      state.userId = "";
      state.openExpirationDateDialog = false;
    },
    handleChangeSubscriptionPlanDialog: (state, action) => {
      state.expirationDateDialog.subscriptionPlanName.value = action.payload;
      state.expirationDateDialog.subscriptionPlanName.error = "";
      state.expirationDateDialog.expired = false;
      state.expirationDateDialog.licenseExpirationDate =
        state.expirationDateDialog.subscriptionPlanList.find(
          (o) => o.subscriptionPlanName === action.payload
        ).expirationDate;
    },
    handleOpenEnableDisableDialog: (state, action) => {
      state.userId = action.payload.id;
      state.isMasterUser = action.payload.isMasterUser;
      state.openEnableDisableDialog = true;
    },
    handleCloseEnableDisableDialog: (state, action) => {
      state.userId = "";
      state.isMasterUser = false;
      state.openEnableDisableDialog = false;
    },
    handleOpenPipedriveImportDialog: (state, action) => {
      state.pipedriveOrganizationCode.value = "";
      state.pipedriveOrganizationCode.error = "";
      state.openPipedriveImportDialog = true;
    },
    handleClosePipedriveImportDialog: (state, action) => {
      state.openPipedriveImportDialog = false;
    },
    handleModelErrors: (state, action) => {
      Object.entries(action.payload).forEach(([field, errors]) => {
        state[field].error = errors[0];
      });
    },
    handleOpenBestowAccount: (state, action) => {
      state.bestowAccount.openPopup = true;
      state.bestowAccount.userId = action.payload.id;
      state.bestowAccount.userName = action.payload.userName;
      state.bestowAccount.organizationName = action.payload.organization;
      state.bestowAccount.licenseAmount = action.payload.licenseAmount;
      state.bestowAccount.additionalUsersAmount =
        action.payload.additionalUsersAmount;
    },
    handleCloseBestowAccount: (state, action) => {
      state.bestowAccount.openPopup = false;
    },
    handleOpenRestoreAccountDialog: (state, action) => {
      state.restoreAccount.userId = action.payload.id;
      state.restoreAccount.userName = action.payload.userName;
      state.restoreAccount.openPopup = true;
    },
    handleCloseRestoreAccountDialog: (state, action) => {
      state.restoreAccount.userId = "";
      state.restoreAccount.userName = "";
      state.restoreAccount.openPopup = false;
    },
    handleOpenUsageReportDialog: (state, action) => {
      state.reportUsage.openPopup = true;
      state.reportUsage.userId = action.payload.id;
      state.reportUsage.userName = action.payload.internalUserName;
    },
    handleCloseUsageReportDialog: (state, action) => {
      state.reportUsage.openPopup = false;
      state.reportUsage.userId = "";
      state.reportUsage.userName = "";
    },
    handleToggleLicensesDialog: (state, action) => {
      state.licenses.openPopup = !state.licenses.openPopup;
      state.licenses.accountInfo = state.licenses.openPopup
        ? action.payload
        : {};
      state.licenses.licensesList = [];
    },
    handleOpenSalesDemoTemplateDialog: (state, action) => {
      state.salesDemoTemplate.openPopup = true;
      state.salesDemoTemplate.userId = action.payload.id;
      state.salesDemoTemplate.userName = action.payload.userName;
    },
    handleCloseSalesDemoTemplateDialog: (state, action) => {
      state.salesDemoTemplate.openPopup = false;
      state.salesDemoTemplate.userId = "";
      state.salesDemoTemplate.userName = "";
    },
    handleOneClickLoginDialog: (state, action) => {
        state.oneClickLogin.openPopup = action.payload.openPopup;
        state.oneClickLogin.oneClickLoginUrl = action.payload.oneClickLoginUrl;
        state.oneClickLogin.userName = action.payload.userName;
        state.oneClickLogin.duration = action.payload.duration;
    },
    handleOpenResendAlertNotificationDialog: (state, action) => {
        state.resendNotifications.openPopup = action.payload.openPopup;
        state.resendNotifications.userId = action.payload.id;
        state.resendNotifications.userName = `${action.payload.firstName} ${action.payload.lastName}`;

        state.resendNotifications.notificationsCount = 0;
        state.resendNotifications.selectedDate = null;
    },
    handleResendAlertNotificationDialog: (state, action) => {
        state.resendNotifications.notificationsCount = action.payload.notificationsCount;
        state.resendNotifications.selectedDate = action.payload.selectedDate;
    },
    handleUpgradeDialog: (state, action) => {
        state.upgradeDialog.openPopup = action.payload.openPopup;

        state.upgradeDialog.organizationName = action.payload.organizationName ?? "";
        state.upgradeDialog.email = action.payload.email ?? "";
        state.upgradeDialog.contactName = action.payload.contactName ?? "";
        state.upgradeDialog.pipedriveOrganizationCode = action.payload.pipedriveOrganizationCode ?? "";

        state.upgradeDialog.subscriptionPlanName.value = "";
        state.upgradeDialog.subscriptionPlanName.error = "";
        state.upgradeDialog.additionalUsers.value = "";
        state.upgradeDialog.additionalUsers.error = "";
        state.upgradeDialog.skipPaymentReason.value = "";
        state.upgradeDialog.skipPaymentReason.error = "";
        state.upgradeDialog.couponCode.value = "";
        state.upgradeDialog.couponCode.error = "";

        state.upgradeDialog.coupon = { ...initialState.upgradeDialog.coupon };
        state.upgradeDialog.applyCoupon = null;

        state.upgradeDialog.paymentType = "Paid"
        state.upgradeDialog.userId = action.payload.userId;
        state.upgradeDialog.organizationId = action.payload.organizationId;
        state.upgradeDialog.paymentCountryCode = action.payload.paymentCountryCode;
    },
    handleChangeSubscriptionPlanUpgradeDialog: (state, action) => {
        state.upgradeDialog.subscriptionPlanName.value = action.payload;
        state.upgradeDialog.subscriptionPlanName.error = '';
        state.upgradeDialog.additionalUsers.value = (state.upgradeDialog.subscriptionPlanList.find(value => (value.subscriptionPlanName === action.payload))?.amountUsers ?? 1).toString();
        state.upgradeDialog.additionalUsers.error = '';
        state.upgradeDialog.planAmount = state.upgradeDialog.subscriptionPlanList.find(value => (value.subscriptionPlanName === action.payload)).planAmount ?? 0
    },
    handleChangeAdditionalUsersUpgradeDialog: (state, action) => {
        state.upgradeDialog.additionalUsers.value = action.payload;
        state.upgradeDialog.additionalUsers.error = '';
    },
    handleChangeCouponCodeUpgradeDialog: (state, action) => {
        state.upgradeDialog.couponCode.value = action.payload;
        state.upgradeDialog.couponCode.error = '';
    },
     handleChangeCouponCodeErrorUpgradeDialog: (state, action) => {
        state.upgradeDialog.couponCode.error = action.payload;
    },
    handleChangePaymentTypeUpgradeDialog: (state, action) => {
        state.upgradeDialog.paymentType = action.payload;
    },
    handleSkipPaymentReasonUpgradeDialog: (state, action) => {
        state.upgradeDialog.skipPaymentReason.value = action.payload;
        state.upgradeDialog.skipPaymentReason.error = "";
    },
    handleSetCouponSubscriptionPlanUpgradeDialog: (state, action) => {
        state.upgradeDialog.subscriptionPlanList = action.payload.subscriptionPlanList;
        state.upgradeDialog.subscriptionPlanName.value = action.payload.subscriptionPlanName;
        state.upgradeDialog.subscriptionPlanName.error = ''
        state.upgradeDialog.additionalUsers.value = (action.payload.subscriptionPlanList.find(value => (value.subscriptionPlanName === action.payload.subscriptionPlanName))?.amountUsers ?? 1).toString();
        state.upgradeDialog.additionalUsers.error = '';
    },
    handleSetCouponUpgradeDialog: (state, action) => {
        state.upgradeDialog.coupon = {...action.payload };
    },
    handleApplyCouponUpgradeDialog: (state, action) => {
        state.upgradeDialog.applyCoupon = action.payload;
    },
    handleErrorsUpgradeDialog: (state, action) => {
        Object.entries(action.payload).forEach(([field, errors]) => { 
            state.upgradeDialog[field].error = errors[0];
        });
    },
    handleClearUpgradeDialog: (state, action) => {
      state.upgradeDialog.subscriptionPlanName = { ...initialState.upgradeDialog.subscriptionPlanName };
      state.upgradeDialog.subscriptionPlanList = state.upgradeDialog.originalSubscriptionPlanList;
      state.upgradeDialog.additionalUsers = { ...initialState.upgradeDialog.additionalUsers };
      state.upgradeDialog.couponCode = { ...initialState.upgradeDialog.couponCode };
      state.upgradeDialog.applyCoupon = null;
      state.upgradeDialog.coupon = { ...initialState.upgradeDialog.coupon };
    }
    
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        adminApi.endpoints.getAllUsersPagedList.matchFulfilled,
        (state, action) => {
          state.loadingList = false;
          state.pagedList.pageNumber = action.payload.pageNumber;
          state.pagedList.pageSize = action.payload.pageSize;
          state.pagedList.pageCount = action.payload.pageCount;
          state.pagedList.totalCount = action.payload.totalCount;
          state.pagedList.orderField = action.payload.orderField;
          state.pagedList.orderDirection = action.payload.orderDirection;
          state.pagedList.items = action.payload.items;
        }
      )
      .addMatcher(
        adminApi.endpoints.getAllUsersPagedList.matchPending,
        (state, action) => {
          state.loadingList = true;
        }
      )
      .addMatcher(
        adminApi.endpoints.getAllUsersPagedList.matchRejected,
        (state, action) => {
          state.loadingList = false;
        }
      )
      .addMatcher(
        adminApi.endpoints.getExpirationDateDetails.matchFulfilled,
        (state, action) => {
          state.expirationDateDialog.licenseExpirationDate =
            action.payload.licenseExpirationDate;
          state.expirationDateDialog.licenseDescription =
            action.payload.licenseDescription;
          state.expirationDateDialog.expired = action.payload.expired;
          state.expirationDateDialog.subscriptionPlanName.value =
            action.payload.subscriptionPlanName;
          state.expirationDateDialog.subscriptionPlanName.error = "";
          state.expirationDateDialog.subscriptionPlanList =
            action.payload.subscriptionPlanList;
        }
      )
      .addMatcher(
        adminApi.endpoints.getOrganizationLicenses.matchFulfilled,
        (state, action) => {
          let hyphens = action.payload.manageLicenses.filter(
            (item) => item.userName === "-"
          );
          let others = action.payload.manageLicenses.filter(
            (item) => item.userName !== "-"
          );

          state.licenses.loadingLicensesList = false;
          state.licenses.licensesList = others.concat(hyphens);
          state.licenses.totalLicenses = action.payload.totalLicenses;
          state.licenses.usedLicenses = action.payload.usedLicenses;
          state.licenses.availableLicenses = action.payload.availableLicenses;
          state.licenses.licenseStatusMessage = action.payload.licenseStatusMessage;
        }
      )
      .addMatcher(
        adminApi.endpoints.getOrganizationLicenses.matchPending,
        (state, action) => {
          state.licenses.loadingLicensesList = true;
        }
      )
      .addMatcher(
        adminApi.endpoints.getOrganizationLicenses.matchRejected,
        (state, action) => {
          state.licenses.loadingLicensesList = false;
        }
      )
      .addMatcher(
        adminApi.endpoints.deleteOrganizationLicense.matchFulfilled,
        (state, action) => {}
      )
      .addMatcher(
        adminApi.endpoints.deleteOrganizationLicense.matchPending,
        (state, action) => {
          state.licenses.loadingLicensesList = true;
        }
      )
      .addMatcher(
        adminApi.endpoints.deleteOrganizationLicense.matchRejected,
        (state, action) => {
          state.licenses.loadingLicensesList = true;
        }
      )
      .addMatcher(
        subscriptionPlanApi.endpoints.getPlans.matchFulfilled, (state, action) => {
            state.upgradeDialog.subscriptionPlanList = action.payload;
            state.upgradeDialog.originalSubscriptionPlanList = action.payload;
        }
      )
  },
});

export const {
  handleChange,
  handleChangeField,
  handleCloseExpirationDateDialog,
  handleOpenExpirationDateDialog,
  handleChangeSubscriptionPlanDialog,
  handleOpenEnableDisableDialog,
  handleCloseEnableDisableDialog,
  handleOpenPipedriveImportDialog,
  handleClosePipedriveImportDialog,
  handleModelErrors,
  handleOpenBestowAccount,
  handleCloseBestowAccount,
  handleOpenRestoreAccountDialog,
  handleCloseRestoreAccountDialog,
  handleOpenUsageReportDialog,
  handleCloseUsageReportDialog,
  handleToggleLicensesDialog,
  handleOpenSalesDemoTemplateDialog,
  handleCloseSalesDemoTemplateDialog,
  handleOneClickLoginDialog,
  handleOpenResendAlertNotificationDialog,
  handleResendAlertNotificationDialog,
  handleUpgradeDialog,
  handleChangeSubscriptionPlanUpgradeDialog,
  handleChangeAdditionalUsersUpgradeDialog,
  handleChangePaymentTypeUpgradeDialog,
  handleErrorsUpgradeDialog,
  handleSkipPaymentReasonUpgradeDialog,
  handleChangeCouponCodeUpgradeDialog,
  handleSetCouponSubscriptionPlanUpgradeDialog,
  handleSetCouponUpgradeDialog,
  handleApplyCouponUpgradeDialog,
  handleChangeCouponCodeErrorUpgradeDialog,
  handleClearUpgradeDialog
} = manageAccountSlice.actions;

export default manageAccountSlice.reducer;
